// src/components/MultiStepForm/AdditionalVehicleForm.js
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import FormField from '../FormField';
import { decodeVin, extractVehicleInfo } from '../../services/vehicleService';

const AdditionalVehicleForm = ({ index, onRemove }) => {
  const { setValue, watch, unregister, formState: { errors } } = useFormContext();
  const prefix = `additional_vehicles[${index}]`;
  const loanPrefix = `additional_loans[${index}]`;

  const vinValue = watch(`${prefix}.vin`);

  // Auto-fill make, model, and year when VIN is 17 characters
  useEffect(() => {
    if (vinValue && vinValue.length === 17) {
      decodeVin(vinValue).then((results) => {
        const vehicle = extractVehicleInfo(results);
        setValue(`${prefix}.make`, vehicle.make || '');
        setValue(`${prefix}.model`, vehicle.model || '');
        setValue(`${prefix}.model_year`, vehicle.model_year || '');
      });
    }
  }, [vinValue, prefix, setValue]);

  const handleRemove = () => {
    // Unregister all fields for this vehicle before removing
    unregister(`${prefix}.vin`);
    unregister(`${prefix}.make`);
    unregister(`${prefix}.model`);
    unregister(`${prefix}.model_year`);
    unregister(`${prefix}.mileage`);
    unregister(`${loanPrefix}.current_monthly_payment`);
    unregister(`${loanPrefix}.current_payoff`);
    unregister(`${loanPrefix}.next_payment_date`);
    unregister(`${loanPrefix}.lien_holder`);
    
    // Call the parent's remove function
    onRemove(index);
  };

  // Helper function to get nested error messages
  const getErrorMessage = (fieldPath) => {
    const pathArray = fieldPath.split('.');
    let error = errors;
    
    for (const key of pathArray) {
      if (error && error[key]) {
        error = error[key];
      } else {
        return null;
      }
    }
    
    return error?.message || null;
  };

// Error display component
const ErrorMessage = ({ fieldPath }) => {
  const errorMessage = getErrorMessage(fieldPath);
  
  if (!errorMessage) return null;
  
  return (
    <div className="text-danger small mt-1" style={{ marginBottom: '10px', width: '100%' }}>
      {errorMessage}
    </div>
  );
};

  // Custom validation functions
  const validateVin = (value) => {
    if (!value) return 'VIN is required';
    if (value.length !== 17) return 'VIN must be exactly 17 characters';
    // Add more VIN validation logic if needed
    return true;
  };

  const validateYear = (value) => {
    if (!value) return 'Year is required';
    const year = parseInt(value);
    const currentYear = new Date().getFullYear();
    if (year < 1900 || year > currentYear + 1) {
      return `Year must be between 1900 and ${currentYear + 1}`;
    }
    return true;
  };

  const validateMileage = (value) => {
    if (!value) return 'Mileage is required';
    const mileage = parseInt(value);
    if (mileage < 0) return 'Mileage cannot be negative';
    if (mileage > 999999) return 'Mileage seems too high';
    return true;
  };

  const validateMonthlyPayment = (value) => {
    if (!value) return 'Monthly payment is required';
    const payment = parseFloat(value.replace(/[,$]/g, ''));
    if (isNaN(payment) || payment < 0) return 'Enter a valid payment amount';
    return true;
  };

  const validatePayoffAmount = (value) => {
    if (!value) return 'Remaining balance is required';
    const amount = parseFloat(value.replace(/[,$]/g, ''));
    if (isNaN(amount) || amount < 0) return 'Enter a valid balance amount';
    return true;
  };

  const validateDate = (value) => {
    if (!value) return 'Next payment date is required';
    const selectedDate = new Date(value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (selectedDate < today) {
      return 'Next payment date cannot be in the past';
    }
    return true;
  };

  return (
    <div className="border p-3 rounded mb-4 bg-light">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">Additional Vehicle #{index + 2}</h5>
        <span
          onClick={handleRemove}
          style={{
            color: 'red',
            cursor: 'pointer',
            fontSize: '1rem',
            textDecoration: 'none'
          }}
          onMouseEnter={(e) => (e.target.style.textDecoration = 'underline')}
          onMouseLeave={(e) => (e.target.style.textDecoration = 'none')}
        >
          Remove Vehicle
        </span>
      </div>

{/* Vehicle Info */}
<div className="row">
  <FormField name={`${prefix}.vin`} label="VIN Number" type="text" required colSpan={6} validation={{ validate: validateVin }} />
  <ErrorMessage fieldPath={`additional_vehicles.${index}.vin`} />
  
  <FormField name={`${prefix}.make`} label="Make" type="text" required colSpan={6} validation={{ required: 'Make is required' }} />
  <ErrorMessage fieldPath={`additional_vehicles.${index}.make`} />
  
  <FormField name={`${prefix}.model`} label="Model" type="text" required colSpan={4} validation={{ required: 'Model is required' }} />
  <ErrorMessage fieldPath={`additional_vehicles.${index}.model`} />
  
  <FormField name={`${prefix}.model_year`} label="Year" type="number" required colSpan={4} validation={{ validate: validateYear }} />
  <ErrorMessage fieldPath={`additional_vehicles.${index}.model_year`} />
  
  <FormField name={`${prefix}.mileage`} label="Mileage" type="number" required colSpan={4} validation={{ validate: validateMileage }} />
  <ErrorMessage fieldPath={`additional_vehicles.${index}.mileage`} />
</div>

{/* Loan Info */}
<div className="row mt-3">
  <FormField name={`${loanPrefix}.current_monthly_payment`} label="Monthly Loan Payment" type="text" required colSpan={6} validation={{ validate: validateMonthlyPayment }} />
  <ErrorMessage fieldPath={`additional_loans.${index}.current_monthly_payment`} />
  
  <FormField name={`${loanPrefix}.current_payoff`} label="Remaining Balance" type="text" required colSpan={6} validation={{ validate: validatePayoffAmount }} />
  <ErrorMessage fieldPath={`additional_loans.${index}.current_payoff`} />
  
  <FormField name={`${loanPrefix}.next_payment_date`} label="Next Payment Date" type="date" required colSpan={6} validation={{ validate: validateDate }} />
  <ErrorMessage fieldPath={`additional_loans.${index}.next_payment_date`} />
  
  <FormField name={`${loanPrefix}.lien_holder`} label="Current Lender" type="text" required colSpan={6} validation={{ required: 'Current lender is required' }} />
  <ErrorMessage fieldPath={`additional_loans.${index}.lien_holder`} />
</div>
    </div>
  );
};

export default AdditionalVehicleForm;